// Nutrition tracking service for Milestone 2

import { where, orderBy } from 'firebase/firestore';
import {
  getCollection,
  addDocument,
  updateDocument,
  deleteDocument,
  getDocumentByUserId,
  getDocumentsByUserAndDateRange,
} from './firebase/firestore.service';
import type {
  NutritionEntry,
  DailyNutrition,
  HydrationEntry,
  TargetRanges,
  RangeCheck,
} from '../types/nutrition';
import { normalizeNutritionEntry, sanitizeMacros, sumMacros, sumHydration } from '../utils/nutrition';
import { getTodayDate, canEditDate, canCreateForDate } from '../utils/date';
import { isDemoMode } from '../demo/demoDetection';

// ITEM 9: Import weekly status cache invalidation
let invalidateWeeklyStatusCacheFn: ((userId: string) => void) | null = null;
export function setWeeklyStatusCacheInvalidator(fn: (userId: string) => void) {
  invalidateWeeklyStatusCacheFn = fn;
}

const NUTRITION_ENTRIES_COLLECTION = 'nutritionEntries';
const HYDRATION_ENTRIES_COLLECTION = 'hydrationEntries';
const TARGET_RANGES_COLLECTION = 'targetRanges';

// SPRINT A: Calorie floor constants for safety
// Male users have a minimum calorie floor of 1500 kcal
// Female/other users have a minimum calorie floor of 1200 kcal
const MALE_CALORIE_FLOOR = 1500;
const FEMALE_CALORIE_FLOOR = 1200;

/**
 * Save a nutrition entry with offline support
 * BUG FIX: Added support for multi-food arrays from AI to prevent silent resets
 */
export async function saveNutritionEntry(
  userId: string,
  entry: any, // Changed to any to handle both Single Object and Array
  date?: string,
): Promise<any> {
  // ITEM 4: Demo Mode - prevent writes (safety net — UI interceptor is primary)
  const { isDemoMode } = await import('../demo');
  if (isDemoMode()) {
    console.warn('[nutrition.service] BLOCKED: Demo mode');
    throw new Error('Demo mode: read-only.');
  }

  // Import offline sync service
  const { saveWithOfflineSupport } = await import('./offlineSync.service');

  // CONTRACT FIX: If entry is an array (multi-food), we save them individually
  if (Array.isArray(entry)) {
    console.log('[nutrition.service] P0 FIX: Processing multi-food array:', entry.length, 'items');
    const promises = entry.map(item => 
      saveWithOfflineSupport(
        'nutrition',
        userId,
        item,
        saveNutritionEntryDirect,
        date,
      )
    );
    // Return the array of results
    return Promise.all(promises);
  }

  // Default: Handle as a single object
  return saveWithOfflineSupport(
    'nutrition',
    userId,
    entry,
    saveNutritionEntryDirect,
    date,
  );
}
/**
 * Save a nutrition entry directly to Firestore (internal use for offline sync)
 */
export async function saveNutritionEntryDirect(
  userId: string,
  entry: Omit<NutritionEntry, 'id' | 'userId' | 'date' | 'timestamp'>,
  date?: string,
): Promise<NutritionEntry> {
  const entryDate = date || getTodayDate();
  
  // Validate that new entries can be created for this date.
  // Today and yesterday are always allowed. Older dates are closed.
  if (!canCreateForDate(entryDate)) {
    console.error('[nutrition.service] ERROR: Cannot create entry for date:', entryDate);
    throw new Error(`Cannot create entries for ${entryDate}. Only today and yesterday are open for new entries.`);
  }
  
  const timestamp = Date.now();
  
  // Remove undefined values (Firestore doesn't accept undefined)
  const cleanedEntry = Object.fromEntries(
    Object.entries(entry).filter(([, value]) => value !== undefined)
  ) as Omit<NutritionEntry, 'id' | 'userId' | 'date' | 'timestamp'>;
  
  const entryData: Omit<NutritionEntry, 'id'> = {
    ...cleanedEntry,
    macros: sanitizeMacros(cleanedEntry.macros),
    hydrationMl: cleanedEntry.hydrationMl ?? 0,
    userId,
    date: entryDate,
    timestamp,
  };
  
  try {
    const result = await addDocument<Omit<NutritionEntry, 'id'>>(
      NUTRITION_ENTRIES_COLLECTION,
      entryData,
    );
    
    // ITEM 9: Invalidate weekly status cache when nutrition data changes
    if (invalidateWeeklyStatusCacheFn) {
      invalidateWeeklyStatusCacheFn(userId);
    }
    
    return result;
  } catch (error) {
    console.error('[nutrition.service] 8. FAILED! Error:', error);
    console.error('[nutrition.service]    - Error type:', error instanceof Error ? error.constructor.name : typeof error);
    console.error('[nutrition.service]    - Error message:', error instanceof Error ? error.message : String(error));
    if (error && typeof error === 'object' && 'code' in error) {
      console.error('[nutrition.service]    - Firebase error code:', (error as { code: string }).code);
    }
    throw error;
  }
}

/**
 * Get nutrition entries for a specific date (includes pending offline entries)
 * ITEM 4: Demo Mode - returns demo data
 */
export async function getNutritionEntriesForDate(
  userId: string,
  date: string, // YYYY-MM-DD
): Promise<NutritionEntry[]> {
  const [firestoreEntries, pendingEntries] = await Promise.all([
    // Get entries from Firestore
    getCollection<NutritionEntry & Record<string, unknown>>(
      NUTRITION_ENTRIES_COLLECTION,
      where('userId', '==', userId),
      where('date', '==', date),
      orderBy('timestamp', 'desc'),
    ),
    // Get pending entries from IndexedDB for this date
    // Skip in demo mode — no IndexedDB, and pending queue can hang
    isDemoMode() ? Promise.resolve([]) :
    (async () => {
      try {
        const { getPendingEntriesByType } = await import('./offlineQueue.service');
        const pending = await getPendingEntriesByType(userId, 'nutrition');
        return pending.filter((p) => p.date === date);
      } catch (error) {
        console.error('[nutrition.service] Failed to get pending entries:', error);
        return [];
      }
    })(),
  ]);

  // Convert Firestore entries
  const normalizedEntries = firestoreEntries.map((entry) =>
    normalizeNutritionEntry(entry as NutritionEntry & Record<string, unknown>),
  );

  // Convert pending entries to NutritionEntry format
  const pendingAsEntries: NutritionEntry[] = pendingEntries.map((pending) => ({
    id: pending.id, // Use pending ID as temporary ID
    userId: pending.userId,
    date: pending.date,
    timestamp: pending.timestamp,
    ...(pending.entryData as Omit<NutritionEntry, 'id' | 'userId' | 'date' | 'timestamp' | 'pending'>),
    pending: true, // Mark as pending
  }));

  // Merge and sort by timestamp
  const allEntries = [...normalizedEntries, ...pendingAsEntries].sort(
    (a, b) => b.timestamp - a.timestamp,
  );

  return allEntries;
}

/**
 * Get nutrition entries for a date range
 */
export async function getNutritionEntriesForDateRange(
  userId: string,
  startDate: string, // YYYY-MM-DD
  endDate: string, // YYYY-MM-DD
): Promise<NutritionEntry[]> {
  const entries = await getDocumentsByUserAndDateRange<NutritionEntry & Record<string, unknown>>(
    NUTRITION_ENTRIES_COLLECTION,
    userId,
    startDate,
    endDate,
  );

  return entries.map((entry) =>
    normalizeNutritionEntry(entry as NutritionEntry & Record<string, unknown>),
  );
}

/**
 * Get daily nutrition summary (aggregated totals)
 * BUG FIX A1/A2: This is the SOURCE OF TRUTH for daily totals
 * Totals are ALWAYS calculated from ALL entries in Firestore - no caching of old values
 * CRITICAL: This function fetches FRESH data from Firestore every time it's called
 * No caching is done here - caching is handled by useDailySummary hook
 */

// Single-flight + TTL cache for getDailyNutrition
// Prevents duplicate Firestore reads when multiple callers fetch for same uid/date
const nutritionFetchCache: Map<string, { promise?: Promise<DailyNutrition>; result?: DailyNutrition; fetchedAt: number }> = new Map();
const NUTRITION_CACHE_TTL_MS = 5000;

export async function getDailyNutrition(
  userId: string,
  date: string, // YYYY-MM-DD
): Promise<DailyNutrition> {
  const cacheKey = `${userId}:${date}`;
  const now = Date.now();
  const cached = nutritionFetchCache.get(cacheKey);

  if (cached?.result && now - cached.fetchedAt < NUTRITION_CACHE_TTL_MS) {
    return cached.result;
  }
  if (cached?.promise) {
    return cached.promise;
  }

  const entry: { promise?: Promise<DailyNutrition>; result?: DailyNutrition; fetchedAt: number } = { fetchedAt: 0 };
  nutritionFetchCache.set(cacheKey, entry);

  entry.promise = (async () => {
  const [entries, hydrationEntries] = await Promise.all([
    getNutritionEntriesForDate(userId, date),
    getHydrationEntriesForDate(userId, date),
  ]);
  
  const normalizedEntries = entries;
  const macroTotals = sumMacros(normalizedEntries);
  const totalFoodHydration = sumHydration(normalizedEntries);

  const totals = {
    totalCalories: macroTotals.calories,
    totalProtein: macroTotals.protein,
    totalCarbs: macroTotals.carbs,
    totalFat: macroTotals.fat,
    totalFibre: macroTotals.fibre,
    totalFoodHydration,
    totalCreatine: normalizedEntries.reduce(
      (sum, entry) => sum + (entry.creatine || 0),
      0,
    ),
    totalCollagen: normalizedEntries.reduce(
      (sum, entry) => sum + (entry.collagen || 0),
      0,
    ),
  };
  
  const totalHydration = hydrationEntries.reduce(
    (sum, entry) => sum + entry.amount,
    0,
  ) + totalFoodHydration;
  
  // BUG FIX A1/A2: Daily totals calculated from source-of-truth
  // This is the SOURCE OF TRUTH - if these numbers are wrong, the issue is in Firestore data
  
    const result: DailyNutrition = {
      date,
      userId,
      ...totals,
      totalHydration,
      entries: normalizedEntries,
      hydrationEntries,
    };
    entry.result = result;
    entry.fetchedAt = Date.now();
    entry.promise = undefined;
    return result;
  })();

  return entry.promise;
}

/**
 * Update a nutrition entry
 * BUG FIX A1/A2: This UPDATE (not CREATE) is critical to prevent ghost calories
 * When editing, old macros must be replaced with new macros in the SAME document
 */
export async function updateNutritionEntry(
  entryId: string,
  updates: Partial<NutritionEntry>,
): Promise<void> {
  // Validate that the entry's date can be edited (days auto-close at 23:59)
  const { getDocument } = await import('./firebase/firestore.service');
  const existing = await getDocument<NutritionEntry>(`${NUTRITION_ENTRIES_COLLECTION}/${entryId}`);
  
  if (!existing) {
    console.error('[nutrition.service] ❌ Entry not found for update:', entryId);
    throw new Error(`Entry ${entryId} not found. Cannot update non-existent entry.`);
  }
  
  if (!canEditDate(existing.date)) {
    throw new Error(`Cannot edit entries for ${existing.date}. Days auto-close at 23:59. Only today can be edited (or yesterday if QA mode is enabled).`);
  }
  
  // BUG FIX: Filter out undefined values before updating Firestore
  // Firestore's updateDoc() doesn't accept undefined values
  const cleanedUpdates = Object.fromEntries(
    Object.entries(updates).filter(([, value]) => value !== undefined)
  ) as Partial<NutritionEntry>;
  
  const newCalories = cleanedUpdates.macros?.calories;
  
  await updateDocument(`${NUTRITION_ENTRIES_COLLECTION}/${entryId}`, cleanedUpdates);
  
  // ITEM 9: Invalidate weekly status cache when nutrition data changes
  if (existing && invalidateWeeklyStatusCacheFn) {
    invalidateWeeklyStatusCacheFn(existing.userId);
  }
  
  // BUG FIX: CRITICAL - Wait for write to be committed and verify before returning
  // This ensures that when refreshDailySummary is called, it will see the updated data
  // Note: getCollection uses getDocsFromServer which bypasses cache
  let verificationPassed = false;
  let retryCount = 0;
  const maxRetries = 5;
  
  while (!verificationPassed && retryCount < maxRetries) {
    try {
      // Wait for write to be committed (increasing delay for retries)
      await new Promise(resolve => setTimeout(resolve, 100 + (retryCount * 50)));
      
      // Re-fetch the entry using getCollection (which uses server reads) to verify update
      const verifyEntries = await getNutritionEntriesForDate(existing.userId, existing.date);
      const verifyEntry = verifyEntries.find(e => e.id === entryId);
      
      if (verifyEntry) {
        const caloriesMatch = verifyEntry.macros?.calories === newCalories;
        
        if (caloriesMatch) {
          verificationPassed = true;
        } else {
          retryCount++;
          if (retryCount >= maxRetries) {
            console.error('[nutrition.service] ❌ VERIFICATION FAILED after ' + maxRetries + ' attempts');
            console.error('[nutrition.service] Expected:', newCalories, 'Got:', verifyEntry.macros?.calories);
          }
        }
      } else {
        retryCount++;
        if (retryCount >= maxRetries) {
          // Don't fail the update if verification fails - the write may still succeed
          break;
        }
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    } catch (verifyError) {
      retryCount++;
      if (retryCount >= maxRetries) {
        console.error('[nutrition.service] Verification fetch failed after ' + maxRetries + ' attempts:', verifyError);
        // Don't fail the update if verification fails - the write may still succeed
        break;
      }
      await new Promise(resolve => setTimeout(resolve, 100));
    }
  }
}

/**
 * Delete a nutrition entry
 * BUG FIX A1/A2: When deleting, ALL calories associated with this entry ID must be removed
 * BUG FIX v28: Handle offline entries - check if ID is pending ID and handle accordingly
 * There should only be ONE entry per ID, so this removes all calories from that meal
 */
export async function deleteNutritionEntry(entryId: string, userId?: string, entryData?: Partial<NutritionEntry>): Promise<void> {
  // ITEM 4: Demo Mode - prevent deletes
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('demo') === '1') {
      console.warn('[nutrition.service] [ITEM 4] BLOCKED: Attempted to delete nutrition entry in demo mode:', { entryId });
      throw new Error('Demo mode: read-only. Cannot delete entries.');
    }
  }
  
  // Bug Fix v28: Check if this is a pending ID (offline entry that hasn't synced yet)
  // Pending IDs have format: nutrition_userId_timestamp_random
  const isPendingId = entryId.startsWith('nutrition_') && entryId.includes('_');
  
  if (isPendingId && userId) {
    
    try {
      // Check if entry is still in offline queue (not synced yet)
      const { getPendingEntriesByType, removePendingEntry } = await import('./offlineQueue.service');
      const pendingEntries = await getPendingEntriesByType(userId, 'nutrition');
      const pendingEntry = pendingEntries.find(p => p.id === entryId);
      
      if (pendingEntry) {
        // Entry is still pending (not synced) - delete from queue
        await removePendingEntry(entryId);
        return;
      } else {
        // Entry was already synced - need to find the server ID
        
        // Extract timestamp from pending ID: nutrition_userId_timestamp_random
        const parts = entryId.split('_');
        let pendingTimestamp: number | null = null;
        if (parts.length >= 3) {
          pendingTimestamp = parseInt(parts[2], 10);
        }
        
        // Get all entries for the date to find the matching synced entry
        // Use entryData if provided, otherwise try to get from current entries
        const searchDate = entryData?.date || getTodayDate();
        const entries = await getNutritionEntriesForDate(userId, searchDate);
        
        // Find entry that matches by timestamp (within 5 second tolerance) and description
        let matchingEntry: NutritionEntry | undefined;
        
        if (pendingTimestamp && entryData?.description) {
          // Match by timestamp and description
          matchingEntry = entries.find(e => 
            e.id && 
            !e.pending && 
            Math.abs((e.timestamp || 0) - pendingTimestamp) < 5000 && // 5 second tolerance
            e.description === entryData.description
          );
        } else if (pendingTimestamp) {
          // Match by timestamp only (less precise but better than nothing)
          matchingEntry = entries.find(e => 
            e.id && 
            !e.pending && 
            Math.abs((e.timestamp || 0) - pendingTimestamp) < 5000
          );
        }
        
        if (matchingEntry?.id) {
          // Delete using server ID (recursive call, but with server ID so won't loop)
          // Pass userId but not entryData to avoid re-entering pending ID logic (server ID won't match pending pattern)
          await deleteNutritionEntry(matchingEntry.id, userId);
          return;
        }
        
        console.warn('[nutrition.service] Could not find synced entry for pending ID:', {
          entryId,
          pendingTimestamp,
          searchDate,
          entriesCount: entries.length,
        });
        // Fall through to try deleting by pending ID (will fail gracefully)
      }
    } catch (error) {
      console.error('[nutrition.service] Error handling pending entry deletion:', error);
      // Fall through to try normal deletion
    }
  }
  
  // Normal deletion path (server ID or fallback)
  // Validate that the entry's date can be edited (days auto-close at 23:59)
  const { getDocument } = await import('./firebase/firestore.service');
  const existing = await getDocument<NutritionEntry>(`${NUTRITION_ENTRIES_COLLECTION}/${entryId}`);
  
  if (!existing) {
    // Bug Fix v28: If entry not found and it was a pending ID, it might have been deleted already
    // or the sync hasn't completed yet - this is acceptable, just return
    if (isPendingId) {
      console.log('[nutrition.service] Pending entry not found - may have been deleted or not yet synced');
      return;
    }
    return; // Entry already deleted, nothing to do
  }
  
  if (!canEditDate(existing.date)) {
    throw new Error(`Cannot edit entries for ${existing.date}. Days auto-close at 23:59. Only today can be edited (or yesterday if QA mode is enabled).`);
  }
  
  await deleteDocument(`${NUTRITION_ENTRIES_COLLECTION}/${entryId}`);
}

/**
 * Save a hydration entry
 */
export async function saveHydrationEntry(
  userId: string,
  amount: number, // ml
  date?: string,
): Promise<HydrationEntry> {
  const entryDate = date || getTodayDate();
  
  // Validate that new entries can be created for this date.
  // Today and yesterday are always allowed. Older dates are closed.
  if (!canCreateForDate(entryDate)) {
    throw new Error(`Cannot create entries for ${entryDate}. Only today and yesterday are open for new entries.`);
  }
  
  const timestamp = Date.now();
  
  const entryData: Omit<HydrationEntry, 'id'> = {
    userId,
    date: entryDate,
    timestamp,
    amount,
  };
  
  return addDocument<Omit<HydrationEntry, 'id'>>(
    HYDRATION_ENTRIES_COLLECTION,
    entryData,
  );
}

/**
 * Get hydration entries for a specific date
 */
export async function getHydrationEntriesForDate(
  userId: string,
  date: string, // YYYY-MM-DD
): Promise<HydrationEntry[]> {
  return getCollection<HydrationEntry>(
    HYDRATION_ENTRIES_COLLECTION,
    where('userId', '==', userId),
    where('date', '==', date),
    orderBy('timestamp', 'desc'),
  );
}

/**
 * Get or create target ranges for a user
 */
export async function getTargetRanges(
  userId: string,
): Promise<TargetRanges | null> {
  return getDocumentByUserId<TargetRanges>(TARGET_RANGES_COLLECTION, userId);
}

/**
 * Auto-create default nutrition targets based on body weight, goal weight, TDEE, and GLP-1 status
 * This is called automatically when TDEE is calculated during onboarding
 */
export async function autoCreateTargetRanges(
  userId: string,
  weightKg: number,
  baseTdee: number,
  goalWeightKg?: number,
  sex?: 'male' | 'female' | 'other',
): Promise<TargetRanges> {
  const existing = await getTargetRanges(userId);

  // Recalculate if fields are missing OR if values are from old formulas
  // Fat old formula: 0.7g/kg = ~63g for 90kg. New: calorieFloor*0.20/9 = ~33g
  // Fibre old values: 30 or 35. New: 25
  const calorieFloorCheck = sex === 'male' ? 1500 : 1200;
  const expectedFatMax = Math.round((calorieFloorCheck * 0.20) / 9) + 5; // ~38g with tolerance
  const isMissingFields = existing != null && (
    !existing.protein?.min ||
    !existing.fat?.min ||
    !existing.fibre?.min ||
    (existing.fat?.min != null && existing.fat.min > expectedFatMax) ||
    (existing.fibre?.min != null && existing.fibre.min > 25)
  );
  if (existing && !isMissingFields) {
    return existing;
  }
  // Capture id before falling through (existing may be stale doc needing update)
  const existingId = existing?.id ?? null;

  // Check GLP-1 status from localStorage (user preference)
  // GLP-1 users need higher protein targets
  let glp1Enabled = false;
  try {
    const glp1 = localStorage.getItem('coretrak.settings.glp1');
    glp1Enabled = glp1 === 'true';
  } catch {
    // Ignore localStorage errors
  }

  // SPRINT A: Determine calorie floor based on sex
  // Male users: 1500 kcal minimum
  // Female/other users: 1200 kcal minimum
  const calorieFloor = sex === 'male' ? MALE_CALORIE_FLOOR : FEMALE_CALORIE_FLOOR;
  
  // Calculate calorie target based on goal weight if available
  // If goal weight is set and different from current, use a deficit-based approach
  let calorieTarget = baseTdee;
  if (goalWeightKg && goalWeightKg < weightKg) {
    // User wants to lose weight - calculate moderate deficit
    // Use TDEE minus moderate deficit (350-500 kcal)
    const moderateDeficit = 400; // Middle of 250-500 range
    calorieTarget = Math.max(calorieFloor, baseTdee - moderateDeficit); // Never go below calorie floor
  } else if (goalWeightKg && goalWeightKg > weightKg) {
    // User wants to gain weight - slight surplus
    calorieTarget = baseTdee + 200;
  }

  // Calculate macro targets based on body weight and GLP-1 status
  // Protein: 1.2–2.0 g/kg per ESPEN guidance
  // GLP-1: 2.0 g/kg (muscle preservation), standard: 1.6 g/kg
  const proteinPerKg = glp1Enabled ? 2.0 : 1.6;
  const proteinMin = Math.round(weightKg * 1.2);
  const proteinMax = Math.round(weightKg * 2.0);
  const proteinTarget = Math.round(weightKg * proteinPerKg);
  
  // Fat: EFSA AMDR lower bound — 20% of calorie floor / 9
  const fatTarget = Math.round((calorieFloor * 0.20) / 9);
  
  // Calculate calories from protein and fat
  const proteinCalories = proteinTarget * 4;
  const fatCalories = fatTarget * 9;
  const remainingCalories = Math.max(0, calorieTarget - (proteinCalories + fatCalories));
  // Carbs fill remaining calories
  const carbsTarget = Math.max(0, Math.round(remainingCalories / 4));
  
  // Default fibre minimum (higher for GLP-1 users)
  const DEFAULT_FIBRE_MIN = 25; // EFSA adequate intake for adults
  
  // Default hydration: 35ml per kg body weight (converted to ml)
  const hydrationMin = sex === 'male' ? 2500 : 2000; // EFSA public-health guidance (ml)
  
  // Default deficit ranges (moderate deficit)
  const dailyDeficitMin = 250;
  const dailyDeficitMax = 500;
  const weeklyDeficitMin = dailyDeficitMin * 7;
  const weeklyDeficitMax = dailyDeficitMax * 7;

  const now = Date.now();
  const effectiveFrom = getTodayDate();

  const ranges: Omit<TargetRanges, 'id' | 'userId' | 'createdAt' | 'updatedAt'> = {
    calories: {
      target: Math.max(calorieFloor, calorieTarget), // SPRINT A: Ensure target respects calorie floor
      min: Math.max(calorieFloor, calorieTarget - 100), // SPRINT A: Never below calorie floor (1500 for male, 1200 for female/other)
      max: Math.max(calorieFloor, calorieTarget) + 100, // Ensure max is at least floor + 100
    },
    protein: {
      target: proteinTarget,
      min: proteinMin,
      max: proteinMax,
    },
    carbs: {
      target: carbsTarget,
      min: Math.max(0, carbsTarget - 20),
      max: carbsTarget + 20,
    },
    // BUG FIX #39: Fat is a single minimum target (like fibre), no max, no range
    fat: {
      min: fatTarget,
    },
    fibre: {
      min: DEFAULT_FIBRE_MIN,
    },
    hydration: {
      min: hydrationMin,
    },
    // BUG FIX #40: Creatine is a pre-populated optional range (3-5g default)
    creatine: {
      min: 3,
      max: 5,
    },
    dailyDeficit: {
      target: Math.round((dailyDeficitMin + dailyDeficitMax) / 2),
      min: dailyDeficitMin,
      max: dailyDeficitMax,
    },
    weeklyDeficit: {
      target: Math.round((weeklyDeficitMin + weeklyDeficitMax) / 2),
      min: weeklyDeficitMin,
      max: weeklyDeficitMax,
    },
  };

  // Stale doc — update in place rather than creating a duplicate
  if (existingId && isMissingFields) {
    await updateDocument(`${TARGET_RANGES_COLLECTION}/${existingId}`, {
      ...ranges,
      updatedAt: now,
      effectiveFrom,
    });
    return { ...(existing as TargetRanges), ...ranges };
  }

  return addDocument<Omit<TargetRanges, 'id'>>(TARGET_RANGES_COLLECTION, {
    ...ranges,
    userId,
    createdAt: now,
    updatedAt: now,
    effectiveFrom,
  });
}

/**
 * Save or update target ranges
 */
export async function saveTargetRanges(
  userId: string,
  ranges: Omit<TargetRanges, 'id' | 'userId' | 'createdAt' | 'updatedAt'>,
): Promise<TargetRanges> {
  const existing = await getTargetRanges(userId);
  const now = Date.now();
  const effectiveFrom = getTodayDate();
  
  if (existing) {
    await updateDocument(`${TARGET_RANGES_COLLECTION}/${existing.id}`, {
      ...ranges,
      updatedAt: now,
      effectiveFrom,
    });
    return {
      ...existing,
      ...ranges,
      updatedAt: now,
      effectiveFrom,
    };
  } else {
    return addDocument<Omit<TargetRanges, 'id'>>(TARGET_RANGES_COLLECTION, {
      ...ranges,
      userId,
      createdAt: now,
      updatedAt: now,
      effectiveFrom,
    });
  }
}

/**
 * Check if a value is in range, near range, or out of range
 */
export function checkRangeStatus(
  value: number,
  min?: number,
  max?: number,
): RangeCheck | null {
  if (min === undefined && max === undefined) {
    return null;
  }

  // BUG FIX #29: Handle minimum-only targets (max is undefined)
  // For minimum-only targets like fat, only check value >= min, no maximum cap
  if (min !== undefined && max === undefined) {
    if (value >= min) {
      return {
        status: 'in-range',
        value,
        min,
        max: undefined, // No max for minimum-only targets
      };
    }
    // Below minimum
    const difference = min - value;
    const tolerance = min * 0.1; // 10% tolerance for "near-range"
    return {
      status: difference <= tolerance ? 'near-range' : 'out-of-range',
      value,
      min,
      max: undefined,
      difference,
    };
  }

  // Handle maximum-only targets (min is undefined)
  if (min === undefined && max !== undefined) {
    if (value <= max) {
      return {
        status: 'in-range',
        value,
        min: undefined,
        max,
      };
    }
    // Above maximum
    const difference = value - max;
    const tolerance = max * 0.1; // 10% tolerance for "near-range"
    return {
      status: difference <= tolerance ? 'near-range' : 'out-of-range',
      value,
      min: undefined,
      max,
      difference,
    };
  }

  // Handle range targets (both min and max defined)
  const resolvedMin = min!;
  const resolvedMax = max!;
  const range = Math.max(resolvedMax - resolvedMin, 1);
  const tolerance = range * 0.1; // 10% tolerance for "near-range"
  
  if (value >= resolvedMin && value <= resolvedMax) {
    return {
      status: 'in-range',
      value,
      min: resolvedMin,
      max: resolvedMax,
    };
  }
  
  const belowMin = value < resolvedMin;
  const difference = belowMin ? resolvedMin - value : value - resolvedMax;
  
  if (difference <= tolerance) {
    return {
      status: 'near-range',
      value,
      min: resolvedMin,
      max: resolvedMax,
      difference,
    };
  }
  
  return {
    status: 'out-of-range',
    value,
    min: resolvedMin,
    max: resolvedMax,
    difference,
  };
}

/**
 * Check all nutrition values against target ranges
 */
export function checkNutritionRanges(
  daily: DailyNutrition,
  targets: TargetRanges,
): {
  calories: RangeCheck | null;
  protein: RangeCheck | null;
  carbs: RangeCheck | null;
  fat: RangeCheck | null;
  hydration: RangeCheck | null;
  creatine: RangeCheck | null;
  fibre: RangeCheck | null;
} {
  return {
    calories: checkRangeStatus(
      daily.totalCalories,
      targets.calories.min,
      targets.calories.max,
    ),
    protein: checkRangeStatus(
      daily.totalProtein,
      targets.protein.min,
      targets.protein.max,
    ),
    carbs: checkRangeStatus(
      daily.totalCarbs,
      0,
      targets.carbs.max,
    ),
    // BUG FIX #39: Fat is a single minimum target (like fibre), no max, no range
    // Treat fat like fibre - only check against min, no max
    fat: checkRangeStatus(
      daily.totalFat,
      targets.fat.min,
      undefined, // No max - fat is minimum-only
    ),
    hydration: checkRangeStatus(
      daily.totalHydration,
      targets.hydration.min,
      targets.hydration.max,
    ),
    // BUG FIX #40: Only check creatine when it's actually logged (totalCreatine > 0)
    // If user doesn't supplement, no warnings should appear
    creatine: daily.totalCreatine > 0
      ? checkRangeStatus(
          daily.totalCreatine,
          targets.creatine.min,
          targets.creatine.max,
        )
      : null, // No check if creatine not logged
    fibre: checkRangeStatus(
      daily.totalFibre,
      targets.fibre.min,
      targets.fibre.max,
    ),
  };
}


/** Invalidate the getDailyNutrition cache for a specific uid/date (call after saves/deletes). */
export function invalidateNutritionCache(userId: string, date: string): void {
  nutritionFetchCache.delete(`${userId}:${date}`);
}
